import {
  AIMDv2WizardState,
  StepValidationResult,
  ProgramStructure,
  GenerationStatus
} from '../types';
import {
  PROGRAM_DESC_MIN_LENGTH,
  PROGRAM_DESC_MAX_LENGTH,
  MIN_REQUIRED_OUTCOMES,
  OUTCOME_MIN_LENGTH,
  OUTCOME_MAX_LENGTH,
  MAX_AUDIENCE_SELECTIONS,
  MAX_TONE_SELECTIONS,
  PROGRAM_PREVIEW_MAX_LENGTH
} from '../constants';

// ---------------------------------------------------------------------------
// Per-step validators
// ---------------------------------------------------------------------------

/** What a step's rules depend on that isn't part of the wizard's own state. */
export interface StepValidationContext {
  /**
   * Whether the org has any member attribute list to pick an audience from:
   * false once the catalog has loaded empty, null while it isn't known yet.
   */
  audienceAvailable?: boolean | null;
}

function validateRequirements(state: AIMDv2WizardState, context: StepValidationContext = {}): StepValidationResult {
  const errors: Record<string, string> = {};
  const { programName, programDescription, audience, tone } = state.requirements;

  if (!programName.trim()) {
    errors.programName = 'Program name is required.';
  }

  const descLen = programDescription.trim().length;
  if (descLen < PROGRAM_DESC_MIN_LENGTH || descLen > PROGRAM_DESC_MAX_LENGTH) {
    errors.programDescription = `Program Description must be between ${PROGRAM_DESC_MIN_LENGTH} and ${PROGRAM_DESC_MAX_LENGTH} characters`;
  }

  // An org with no attribute lists has no audience to pick: who joins is chosen on
  // the Members step. Until the catalog is known the audience stays required.
  if (audience.length === 0) {
    if (context.audienceAvailable !== false) errors.audience = 'Select at least one audience.';
  } else if (audience.length > MAX_AUDIENCE_SELECTIONS) {
    errors.audience = `Select up to ${MAX_AUDIENCE_SELECTIONS} audience attributes.`;
  }

  if (tone.length === 0) {
    errors.tone = 'Select at least one tone.';
  } else if (tone.length > MAX_TONE_SELECTIONS) {
    errors.tone = `Select up to ${MAX_TONE_SELECTIONS} tones.`;
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

function validateOutcomes(state: AIMDv2WizardState): StepValidationResult {
  const errors: Record<string, string> = {};
  const { outcomes } = state.outcomes;

  const filledOutcomes = outcomes.filter((o) => o.value.trim().length > 0);
  if (filledOutcomes.length < MIN_REQUIRED_OUTCOMES) {
    errors._global = `At least ${MIN_REQUIRED_OUTCOMES} goals are required.`;
  }

  outcomes.forEach((o, idx) => {
    const trimmed = o.value.trim();
    if (!trimmed) {
      if (idx < MIN_REQUIRED_OUTCOMES)
        errors[
          `outcome_${idx}`
        ] = `Each goal must be between ${OUTCOME_MIN_LENGTH} and ${OUTCOME_MAX_LENGTH} characters.`;
      return;
    }
    if (trimmed.length < OUTCOME_MIN_LENGTH) {
      errors[
        `outcome_${idx}`
      ] = `Each goal must be between ${OUTCOME_MIN_LENGTH} and ${OUTCOME_MAX_LENGTH} characters.`;
    } else if (trimmed.length > OUTCOME_MAX_LENGTH) {
      errors[
        `outcome_${idx}`
      ] = `Each goal must be between ${OUTCOME_MIN_LENGTH} and ${OUTCOME_MAX_LENGTH} characters.`;
    }
  });

  return { isValid: Object.keys(errors).length === 0, errors };
}

function validateDocuments(state: AIMDv2WizardState): StepValidationResult {
  const errors: Record<string, string> = {};
  // Documents are optional, but a SELECTED document must be fully indexed before
  // continuing — only `indexed` docs are sent as `orgDocsIds` to the generator.
  // Letting a still-processing selection through would silently drop it from the
  // RAG payload (and an empty payload used to ground the generation on the whole
  // org corpus). Block until every selected doc is indexed.
  const allDocs = [
    ...state.documents.uploadedDocuments,
    ...state.documents.previousDocuments
  ];
  const pending = allDocs.filter((d) => d.selected && d.processingStatus !== 'indexed');
  if (pending.length > 0) {
    const names = pending.map((d) => d.title).filter(Boolean);
    errors._global =
      names.length > 0
        ? `Wait for selected document(s) to finish indexing before continuing: ${names.join(', ')}.`
        : 'Wait for selected documents to finish indexing before continuing.';
  }
  return { isValid: Object.keys(errors).length === 0, errors };
}

function validatePreview(state: AIMDv2WizardState): StepValidationResult {
  const errors: Record<string, string> = {};
  const preview = state.preview.generatedPreview;
  if (!preview) {
    errors._global = 'Preview must be generated before proceeding.';
  } else if (preview.length > PROGRAM_PREVIEW_MAX_LENGTH) {
    errors._global = `Preview must be at most ${PROGRAM_PREVIEW_MAX_LENGTH} characters.`;
  }
  return { isValid: Object.keys(errors).length === 0, errors };
}

function validateStructure(state: AIMDv2WizardState): StepValidationResult {
  const errors: Record<string, string> = {};
  const structure = state.structure.generatedStructure as ProgramStructure | null;

  if (!structure || structure.modules.length === 0) {
    errors._global = 'At least one module is required.';
    return { isValid: false, errors };
  }

  for (let i = 0; i < structure.modules.length; i++) {
    const mod = structure.modules[i];
    if (!mod.title.trim()) {
      errors[`module_${i}`] = `Module ${i + 1} needs a title.`;
    }
    if (mod.lessons.length === 0) {
      errors[`module_${i}_lessons`] = `Module ${i + 1} needs at least one lesson.`;
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

function validateContent(state: AIMDv2WizardState): StepValidationResult {
  const errors: Record<string, string> = {};
  const content = state.content.generatedContent as GenerationStatus | null;

  if (!content) {
    errors._global = 'Build the lessons before moving on.';
    return { isValid: false, errors };
  }

  if (content.status !== 'completed') {
    errors._global = 'Elliott is still writing the lessons.';
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

function validateMembers(_state: AIMDv2WizardState): StepValidationResult {
  return { isValid: true, errors: {} };
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

const VALIDATORS: Array<(state: AIMDv2WizardState, context: StepValidationContext) => StepValidationResult> = [
  validateRequirements,
  validateOutcomes,
  validateDocuments,
  validatePreview,
  validateStructure,
  validateContent,
  validateMembers
];

export function validateStep(
  state: AIMDv2WizardState,
  stepIndex: number,
  context: StepValidationContext = {}
): StepValidationResult {
  const validator = VALIDATORS[stepIndex];
  if (!validator) return { isValid: true, errors: {} };
  return validator(state, context);
}
